import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, useField } from 'react-final-form-hooks';
import { useFirestoreConnect, isLoaded } from 'react-redux-firebase';

import { useTheme } from '../../ThemeContext';

import {
  profileEditInit,
  profileEdit,
} from '../../actions/Profile/ProfileActions';

import ProfileManageAsideTabs from '../ProfileManageAsideTabs';
import Select from '../Select';
import ProfilePhotoChanger from '../ProfilePhotoChanger';

import {
  Form,
  Header,
  Title,
  Content,
  Textbox,
  Text,
  Footer,
  Button,
  Box,
  Aside,
  Main,
  Side,
  MainInner,
  Group,
  Subtitle,
  Inputbox,
  Input,
} from './ProfileManageBasic.style';

import roles from '../../roles';

const ProfileManageBasic = ({
  currentTab,
  tabs = [],
  handleClose = () => {},
  handleChangeTab = () => {},
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const firebaseStore = useSelector((state) => state.firebase);
  const { auth, profile } = firebaseStore;
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const profileCompany = useSelector(
    (state) => state.firestore.data['profileCompany']
  );
  const profileCompanyRole =
    profileCompany &&
    profileCompany.admins &&
    profileCompany.admins.length > 0 &&
    profileCompany.admins.find((admin) => admin.id === auth.uid)
      ? 'isAdmin'
      : profileCompany &&
        profileCompany.users &&
        profileCompany.users.length > 0 &&
        profileCompany.users.find((user) => user.id === auth.uid)
      ? 'isUser'
      : undefined;
  const authRole = roles.find(
    (role) => profileCompanyRole && role.id === profileCompanyRole
  );

  const profileEditStore = useSelector((state) => state.profileEditStore);
  const { isLoading, isSuccess, isError, error } = profileEditStore;
  const dispatch = useDispatch();

  const validate = (values) => {
    const errors = {};
    if (!values.fullname)
      errors.fullname = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });
    if (
      values.fullname &&
      !/^[\w'\-,.][^0-9_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<>;:[\]]{2,}$/.test(
        values.fullname
      )
    )
      errors.fullname = intl.formatMessage({
        id: 'FORM.VALIDATION.INVALID_FORMAT',
      });
    return errors;
  };

  const onSubmit = (values) => {
    try {
      const newProfileData = {
        fullname: values.fullname,
        role:
          authRole &&
          authRole.privileges &&
          authRole.privileges['MANAGE_PROFILE_ROLE'] &&
          values.role
            ? values.role === 'isAdmin'
              ? 'admins'
              : values.role === 'isUser'
              ? 'users'
              : undefined
            : undefined,
        address: values.address,
        city: values.city,
        zipCode: values.zipCode,
        photoData: values.photoData,
      };
      dispatch(profileEdit(newProfileData));
    } catch {}
  };

  const { form, handleSubmit, pristine, submitting, invalid } = useForm({
    onSubmit,
    validate,
    initialValues: {
      fullname: profile.full_name,
      role: profileCompanyRole,
      address: profile.address,
      city: profile.city,
      zipCode: profile.zip,
    },
  });

  const fullnameField = useField('fullname', form);
  const roleField = useField('role', form);
  const addressField = useField('address', form);
  const cityField = useField('city', form);
  const zipCodeField = useField('zipCode', form);
  const photoDataField = useField('photoData', form);

  const firestoreConnect = [];
  if (isProfileCompany)
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      storeAs: 'profileCompany',
    });
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    dispatch(profileEditInit());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Form onSubmit={handleSubmit}>
      <Header theme={theme.profileManageBasic.header}>
        <Title theme={theme.profileManageBasic.title}>
          {intl.formatMessage({
            id: 'PROFILE_MANAGE_BASIC.EDIT_PROFILE',
          })}
        </Title>
      </Header>
      <Content>
        <Box>
          <Aside>
            <ProfileManageAsideTabs
              currentTab={currentTab}
              tabs={tabs}
              handleChangeTab={handleChangeTab}
            />
          </Aside>
          <Main>
            <MainInner>
              <Group>
                <Subtitle theme={theme.profileManageBasic.subtitle}>
                  {intl.formatMessage({
                    id: 'PROFILE_MANAGE_BASIC.USER_PROFILE',
                  })}
                </Subtitle>
                <Inputbox>
                  <Input
                    theme={theme.input.default}
                    {...fullnameField.input}
                    placeholder={intl.formatMessage({
                      id: 'PROFILE_MANAGE_BASIC.INPUT.FULL_NAME',
                    })}
                    invalid={
                      fullnameField.meta.touched && fullnameField.meta.error
                    }
                  />
                </Inputbox>
                {isProfileCompany ? (
                  isLoaded(profileCompany) ? (
                    profileCompanyRole ? (
                      <Inputbox>
                        {authRole &&
                        authRole.privileges &&
                        authRole.privileges['MANAGE_PROFILE_ROLE'] ? (
                          <Select
                            value={roleField.input.value}
                            options={
                              roles &&
                              roles.length > 0 &&
                              roles
                                .filter((role) => {
                                  return (
                                    authRole.nomination &&
                                    authRole.nomination.includes(role.id)
                                  );
                                })
                                .map((role) => {
                                  return {
                                    ...role,
                                    value: role.id,
                                    label: intl.formatMessage({
                                      id: `ROLE.${role.name}`,
                                      defaultMessage: role.name,
                                    }),
                                  };
                                })
                            }
                            placeholderMessage={
                              'PROFILE_MANAGE_BASIC.INPUT.ROLE'
                            }
                            onChange={(value) => {
                              roleField.input.onChange(value);
                            }}
                            onBlur={() => {
                              roleField.input.onBlur();
                            }}
                            invalid={
                              roleField.meta.touched && roleField.meta.error
                            }
                          />
                        ) : (
                          <Input
                            theme={theme.input.default}
                            placeholder={intl.formatMessage({
                              id: 'PROFILE_MANAGE_BASIC.INPUT.ROLE',
                            })}
                            disabled={true}
                            readOnly={true}
                            value={intl.formatMessage({
                              id: `ROLE.${authRole.name}`,
                              defaultMessage: authRole.name,
                            })}
                          />
                        )}
                      </Inputbox>
                    ) : (
                      <Inputbox>
                        <Input
                          theme={theme.input.default}
                          placeholder={intl.formatMessage({
                            id: 'PROFILE_MANAGE_BASIC.INPUT.ROLE',
                          })}
                          disabled={true}
                          readOnly={true}
                          value={intl.formatMessage({
                            id: `ROLE.NO_ROLE`,
                          })}
                        />
                      </Inputbox>
                    )
                  ) : (
                    <Inputbox>
                      <Input
                        theme={theme.input.default}
                        placeholder={intl.formatMessage({
                          id: 'PROFILE_MANAGE_BASIC.INPUT.ROLE',
                        })}
                        disabled={true}
                        readOnly={true}
                        value={intl.formatMessage({
                          id: `PROFILE_MANAGE_BASIC.ROLE.LOADING`,
                        })}
                      />
                    </Inputbox>
                  )
                ) : (
                  ''
                )}
                <Inputbox>
                  <Input
                    theme={theme.input.default}
                    placeholder={intl.formatMessage({
                      id: 'PROFILE_MANAGE_BASIC.INPUT.EMAIL_ADDRESS',
                    })}
                    disabled={true}
                    readOnly={true}
                    value={auth.email}
                  />
                </Inputbox>
              </Group>
              <Group>
                <Subtitle theme={theme.profileManageBasic.subtitle}>
                  {intl.formatMessage({
                    id: 'PROFILE_MANAGE_BASIC.USER_INFORMATION',
                  })}
                </Subtitle>
                <Inputbox>
                  <Input
                    theme={theme.input.default}
                    {...addressField.input}
                    placeholder={intl.formatMessage({
                      id: 'PROFILE_MANAGE_BASIC.INPUT.ADDRESS',
                    })}
                    invalid={
                      addressField.meta.touched && addressField.meta.error
                    }
                  />
                </Inputbox>
                <Inputbox>
                  <Input
                    theme={theme.input.default}
                    {...cityField.input}
                    placeholder={intl.formatMessage({
                      id: 'PROFILE_MANAGE_BASIC.INPUT.CITY',
                    })}
                    invalid={cityField.meta.touched && cityField.meta.error}
                  />
                </Inputbox>
                <Inputbox>
                  <Input
                    theme={theme.input.default}
                    {...zipCodeField.input}
                    placeholder={intl.formatMessage({
                      id: 'PROFILE_MANAGE_BASIC.INPUT.ZIP_CODE',
                    })}
                    invalid={
                      zipCodeField.meta.touched && zipCodeField.meta.error
                    }
                  />
                </Inputbox>
              </Group>
            </MainInner>
            <Side>
              <ProfilePhotoChanger
                input={photoDataField.input}
                currentPhoto={profile.avatar}
              />
            </Side>
          </Main>
        </Box>
      </Content>
      {isSuccess && (
        <Textbox>
          <Text theme={theme.profileManageBasic.text.default}>
            {intl.formatMessage({
              id: `PROFILE_MANAGE_BASIC.TEXT.THE_PROFILE_HAS_BEEN_SUCCESSFULLY_UPDATED`,
            })}
          </Text>
        </Textbox>
      )}
      {isError && (
        <Textbox>
          <Text theme={theme.profileManageBasic.text.error}>
            {intl.formatMessage({
              id: `PROFILE_MANAGE_BASIC.TEXT.ERROR.${error}`,
              defaultMessage: error,
            })}
          </Text>
        </Textbox>
      )}
      <Footer>
        <Button
          type='button'
          theme={theme.button.alt}
          disabled={isLoading}
          onClick={() => {
            handleClose();
          }}
        >
          {intl.formatMessage({ id: 'PROFILE_MANAGE_BASIC.CANCEL' })}
        </Button>
        <Button
          theme={theme.button.default}
          disabled={submitting || pristine || invalid || isLoading}
        >
          {isLoading
            ? intl.formatMessage({ id: 'PROFILE_MANAGE_BASIC.PLEASE_WAIT' })
            : intl.formatMessage({
                id: 'PROFILE_MANAGE_BASIC.EDIT',
              })}
        </Button>
      </Footer>
    </Form>
  );
};

ProfileManageBasic.propTypes = {
  currentTab: PropTypes.string,
  tabs: PropTypes.array,
  handleClose: PropTypes.func,
  handleChangeTab: PropTypes.func,
};

export default ProfileManageBasic;
